import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatLegacySelect as MatSelect,
  MatLegacySelectChange as MatSelectChange
} from '@angular/material/legacy-select';
import { RefTypeItem } from '@sd-portal/shared/ref-type-item';
import { ValueAccessorBase, ValueAccessorProvider } from '@sd-portal/shared/value-accessor';

import { ReferenceService } from '../../services/reference.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, map, pairwise } from 'rxjs/operators';
import { untilViewDestroyed } from '@sd-portal/shared/rxjs';

@Component({
  selector: 'sd-reference-type-select',
  templateUrl: './reference-type-select.component.html',
  styleUrls: ['./reference-type-select.component.scss'],
  providers: [ValueAccessorProvider.provider(RefTypeItemSelectComponent)]
})
export class RefTypeItemSelectComponent
  extends ValueAccessorBase<number | string>
  implements OnInit, OnDestroy
{
  @Input() sdpControl: FormControl = new FormControl();
  @Input() referenceTypeName: string;
  @Input() matPlaceholder = 'Type';
  @Input() defaultOptionText = '';
  @Input() hasSelectOption = true;
  @Input() isRequired = false;
  @Input() includeSystem = false;
  @Input() selectedValue: number | null = null;
  @Input() referenceTypeList: RefTypeItem[] = [];
  @Input() isMultiple = false;
  @Input() isOrderById = false;

  get disabled() {
    return this.matSelect.disabled;
  }
  @Input() set disabled(isDisabled: boolean) {
    this.manualDisable.next(isDisabled);
  }

  @Output() itemSelected = new EventEmitter<RefTypeItem>();
  @Output() itemSelectedObj = new EventEmitter<any>(); // eslint-disable-line @typescript-eslint/no-explicit-any
  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() prevSelectedValue = new EventEmitter<number | string>();

  @ViewChild('matSelect', { read: MatSelect, static: true })
  matSelect: MatSelect;

  manualDisable = new BehaviorSubject<boolean>(false);
  manualDisable$ = this.manualDisable.asObservable();
  inheritedDisable = new BehaviorSubject<boolean>(false);
  inheritedDisable$ = this.inheritedDisable.asObservable();

  isDisabled$ = combineLatest([this.manualDisable$, this.inheritedDisable$]).pipe(
    map(([manual, inherited]) => {
      return manual || inherited;
    })
  );

  constructor(private readonly referenceTypeService: ReferenceService) {
    super();
  }

  /* life-cycle */
  ngOnInit() {
    if (this.referenceTypeName) {
      this.buildRefTypeItemList();
    } else if (this.referenceTypeList && this.referenceTypeList.length > 0) {
      this.filterOnActiveAndSelected(this.referenceTypeList);
      this.getRefObjById(this.sdpControl.value);
    }

    this.sdpControl.valueChanges
      .pipe(debounceTime(10), pairwise())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .subscribe(([prev, next]: [any, any]) => {
        this.itemSelected.emit(next);
        this.getRefObjById(next);
        this.prevSelectedValue.emit(prev);
      });

    this.isDisabled$
      .pipe(debounceTime(10), untilViewDestroyed(this))
      .subscribe((disableStatus: boolean) => {
        if (this.matSelect) {
          if (disableStatus) {
            this.sdpControl.disable();
          } else {
            this.sdpControl.enable();
          }
        }
      });
  }

  ngOnDestroy(): void {
    //Destroy
  }

  /* public */
  reset() {
    this.sdpControl.reset();
  }

  setDisabledState(isDisabled: boolean): void {
    this.inheritedDisable.next(isDisabled);
  }

  /* private */
  private buildRefTypeItemList() {
    this.referenceTypeService
      .getReferenceItems(this.referenceTypeName, false, this.includeSystem)
      .pipe(untilViewDestroyed(this))
      .subscribe(res => {
        this.filterOnActiveAndSelected(res);
        this.getRefObjById(this.sdpControl.value);
        if (this.isOrderById) {
          this.orderById();
        }
      });
  }

  private getRefObjById(id: number) {
    if (id && typeof id === 'number' && this.referenceTypeList) {
      this.itemSelectedObj.emit(this.referenceTypeList.find(ref => ref.id === id));
    } else {
      this.itemSelectedObj.emit(null);
    }
  }

  private orderById() {
    this.referenceTypeList = this.referenceTypeList.sort((a, b) => a.id - b.id);
  }

  private filterOnActiveAndSelected(refTypes: RefTypeItem[]) {
    this.referenceTypeList = refTypes.filter(
      refType =>
        refType.active ||
        (this.sdpControl.value && refType.id === this.sdpControl.value) ||
        (this.selectedValue && refType.id === this.selectedValue)
    );
  }
}
